import { SupportedLanguages } from '@carlito/bookito-client';
import { setupShadowDomStyle } from '@carlito/css';
import '@carlito/react-intl';
import ReactDOM from 'react-dom';
import BookitoEventModal, { BookitoEventModalProps } from './BookitoEventModal';
import BookitoWidget, { BookitoWidgetProps } from './BookitoWidget';
import { BookitoWidgetViewMode } from './types';
/**
 Ensure that all CSS modules are imported here and included in the "additionalStyles" .This serves as a temporary solution to loading styles in the shadow root.
 The alternative approach involves registering the style to global variable, but this method is currently not functioning as expected and requires further investigation.
 * */
import AnimatedSuccessCheckmarkStyles from './BookitoForm/FormSubmitButton/AnimatedSuccessCheckmark/index.module.css';
import BookitoWidgetProvider from './BookitoWidgetProvider/BookitoWidgetProvider';
import reactCalendarStyles from './calendar-styles.css';
import styles from './index.module.css';

console.log('🚀 Bookito Widget initialized! Visit https://carlito-app.com for more details!');

type InitializeBookitoWidget = {
  container: HTMLElement | string;
  customCss?: string;
} & BookitoWidgetProps;

type OpenBookitoEventModalArg = {
  container?: HTMLElement | string;
  onCloseModal?: () => void;
} & Omit<BookitoEventModalProps, 'openModal'>;

/**
 * This function is used to extract all styles from the shadow doms and insert them into the
 * shadow dom of the widget. This is needed because the style-loader does not allow
 * to mount styles more than once, which is super annoying and weird.
 */
function getStyleLoaderStyles() {
  // Get all shadow roots
  const shadowRoots = Array.from(document.querySelectorAll('*'))
    .map((el) => el.shadowRoot)
    .filter((root) => root != null) as ShadowRoot[];

  // Search shadows and extract styles
  const allStyles = shadowRoots
    .map((root) => Array.from(root.querySelectorAll('style[data-style-loader]')))
    .reduce((acc, tags) => {
      return [...acc, ...tags.map((tag) => tag.innerHTML)];
    }, [] as string[]);

  if (allStyles.length === 0) {
    return null;
  }

  const styleTag = document.createElement('style');
  styleTag.setAttribute('data-style-loader', '');
  styleTag.innerHTML = allStyles.join('');

  return styleTag;
}

if (typeof window !== 'undefined') {
  const createRoot = ({ container, ...props }: InitializeBookitoWidget): HTMLDivElement => {
    const elem = typeof container === 'string' ? document.querySelector(container) : container;
    if (!elem) {
      throw new Error('No container defined!' + container);
    }

    if (!props.authId) {
      throw new Error('No auth id param defined!');
    }
    const shadowRoot = elem.shadowRoot ?? elem.attachShadow({ mode: 'open' });
    const div = document.createElement('div');
    div.style.fontFamily = 'var(--theme-font-family-sans)';
    shadowRoot.append(div);

    const stylesToInsert = getStyleLoaderStyles();
    if (props.customCss) {
      const styleElement = document.createElement('style');
      styleElement.innerHTML = props.customCss;
      shadowRoot.append(styleElement);
    }

    if (stylesToInsert) {
      // copy styles from style-loader
      shadowRoot.append(stylesToInsert);
      setupShadowDomStyle({
        shadowRoot,
        hotelId: props.authId,
        additonalStyles: [reactCalendarStyles, AnimatedSuccessCheckmarkStyles],
      }); //Todo: added this line as a temp fix  to be able to load theme variables ,
    } else {
      setupShadowDomStyle({
        shadowRoot,
        hotelId: props.authId,
        additonalStyles: [reactCalendarStyles, AnimatedSuccessCheckmarkStyles],
      });
      (styles as any).use({ target: shadowRoot });
    }

    return div;
  };

  const bookitoWidget = {
    initialize({ ...props }: InitializeBookitoWidget) {
      const rootElement = createRoot(props);
      ReactDOM.render(
        <BookitoWidgetProvider hotelId={props.authId} language={props?.language}>
          <BookitoWidget {...props} />
        </BookitoWidgetProvider>,
        rootElement
      );
    },
    openBookitoModal(props: OpenBookitoEventModalArg) {
      const rootElement = createRoot({
        container: props.container ?? window.document.body,
        ...props,
      });
      const closeModal = () => {
        ReactDOM.unmountComponentAtNode(rootElement);
        rootElement.parentNode?.removeChild(rootElement);
        props.onCloseModal && props.onCloseModal();
      };
      ReactDOM.render(
        <BookitoWidgetProvider hotelId={props.authId} language={props?.language}>
          <BookitoEventModal openModal={true} {...props} onCloseModal={closeModal} />
        </BookitoWidgetProvider>,
        rootElement
      );
    },
  };

  (window as any).bookitoWidget = bookitoWidget;

  if (!customElements.get('bookito-widget')) {
    class BookitoWidgetWebComponent extends HTMLElement {
      private _container;
      constructor() {
        super();

        this._container = this;
      }

      connectedCallback() {
        bookitoWidget.initialize({
          container: this._container,
          authId: this.getAttribute('auth-id') || '',
          categoryId: this.getAttribute('category-id') || '',
          title: this.getAttribute('title') || '',
          language: this.getAttribute('language') as SupportedLanguages,
          viewMode:
            (this.getAttribute('viewMode') as BookitoWidgetViewMode) ||
            (this.getAttribute('view-mode') as BookitoWidgetViewMode) ||
            'Date',
          customCss: this.getAttribute('custom-css') || this.getAttribute('customCss') || '',
          excludeNonBookable: this.getAttribute('exclude-non-bookable') === 'true' || false,
        });
      }
    }

    customElements.define('bookito-widget', BookitoWidgetWebComponent);
  }
}

export { BookitoEventModal, BookitoWidgetProvider };
export type { BookitoWidgetViewMode };
export default BookitoWidget;
